import React, { useState, useEffect } from "react";
import { useLanguage } from "../../hooks/useLanguage";
import ThemeToggle from "../theme/ThemeToggle";
// Import logo correctly
import { X, Calendar, Sun, Sparkles, Flame, PartyPopper } from "lucide-react";
import logo from "../../assets/images/logo.png";



export default function Navbar() {
  const { t, language, setLanguage } = useLanguage();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showMidsummerNotice, setShowMidsummerNotice] = useState(true);
  

  
  // Handle body scroll locking
  useEffect(() => {
    if (showMidsummerNotice) {
      // Save current scroll position before locking
      const scrollY = window.scrollY;
      
      // Lock scroll
      document.body.classList.add('no-scroll');
      document.body.style.top = `-${scrollY}px`;
    } else {
      // Get scroll position from body top property
      const scrollY = document.body.style.top;
      
      // Enable scroll
      document.body.classList.remove('no-scroll');
      document.body.style.top = '';
      
      // Restore scroll position
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }
    
    // Cleanup function to ensure scroll is re-enabled when component unmounts
    return () => {
      const scrollY = document.body.style.top;
      document.body.classList.remove('no-scroll');
      document.body.style.top = '';
      
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    };
  }, [showMidsummerNotice]);

  // Add scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      // Add shadow and background opacity when scrolled
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogoClick = () => {
    window.location.reload();
  };

  // Dynamic styles based on scroll position
  const navbarStyles = isScrolled
    ? "bg-white/95 dark:bg-gray-900/95 shadow-lg backdrop-blur-sm"
    : "bg-transparent dark:bg-transparent";

  // Text styles with better visibility in light mode
  const textStyles = isScrolled
    ? "text-gray-800 dark:text-gray-100"
    : "text-gray-800 dark:text-white"; // Changed from text-white to text-gray-800 for light mode

  // Link styles with better visibility in light mode
  const linkStyles = isScrolled
    ? "text-gray-800 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-400"
    : "text-gray-800 font-semibold dark:text-white hover:text-amber-600 dark:hover:text-white"; // Enhanced contrast for light mode

  return (
    <>
  

      {/* Fully booked banner */}
      {/* <div className="bg-red-50 border-b border-red-500 text-center py-2">
        <p className="text-red-800 text-sm font-medium">
          {language === "en"
            ? "We are fully booked until 20.05. Please email us at waveofflavors@gmail.com for bookings."
            : "Olemme täyteen varattuja 20.05 asti. Ole hyvä ja lähetä sähköpostia osoitteeseen waveofflavors@gmail.com varauksia varten."}
        </p>
      </div> */}

      {/* Open notification banner */}
      {/* <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-center py-4 shadow-lg relative overflow-hidden border-t-2 border-b-2 border-white/20">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle,_white_1px,_transparent_1px)] bg-[length:12px_12px]"></div>
        <div className="px-4 mx-auto max-w-7xl relative z-10">
          <p className="text-white text-lg font-bold flex items-center justify-center">
            <span className="bg-white text-green-600 p-1.5 rounded-full mr-3 shadow-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </span>
            <span className="tracking-wide">
              {language === "en"
                ? "We are open TODAY (09.06.2025) after 16:00. Welcome!"
                : "Olemme avoinna TÄNÄÄN (09.06.2025) klo 16:00 jälkeen. Tervetuloa!"}
            </span>
          </p>
        </div>
      </div> */}

      <nav className={`w-full z-50 transition-all duration-300 ${navbarStyles}`}>
        <div className="max-w-7xl mx-auto p-3 px-6 sm:px-8 lg:px-10">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src={logo}
                alt="Wave Of Flavors Logo"
                className="h-16 w-auto transition-transform duration-500 ease-in-out transform hover:scale-110"
                onClick={handleLogoClick}
              />
              <span
                className={`ml-3 text-2xl font-bold ${textStyles} transition-colors`}
              >
                Wave of Flavors
              </span>
            </div>

            {/* Desktop Menu - with improved visibility */}
            <div className="hidden md:flex items-center space-x-10">
              <a
                href="#menu"
                className={`text-lg ${linkStyles} transition duration-300 transform hover:scale-110`}
              >
                {t("nav.menu")}
              </a>
              <a
                href="#book"
                className={`text-lg ${linkStyles} transition duration-300 transform hover:scale-110`}
              >
                {t("nav.book")}
              </a>
              <a
                href="#about"
                className={`text-lg ${linkStyles} transition duration-300 transform hover:scale-110`}
              >
                {t("nav.about")}
              </a>
              <a
                href="#contact"
                className={`text-lg ${linkStyles} transition duration-300 transform hover:scale-110`}
              >
                {t("nav.contact")}
              </a>
            </div>

            {/* Theme Toggle and Language Button */}
            <div className="hidden md:flex items-center space-x-4">
              <ThemeToggle />
              <button
                onClick={() => setLanguage(language === "en" ? "fi" : "en")}
                className="bg-amber-600 text-white px-4 py-2 rounded-md hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600 transition-colors"
              >
                {language === "en" ? "FI" : "EN"}
              </button>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className={`md:hidden ${textStyles} focus:outline-none`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d={
                    isMobileMenuOpen
                      ? "M6 18L18 6M6 6l12 12"
                      : "M4 6h16M4 12h16M4 18h16"
                  }
                ></path>
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-lg p-4 shadow-md">
              <div className="space-y-4">
                <a
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    window.location.href = "#menu";
                  }}
                  className="block text-lg text-gray-800 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-400 transition-transform transform hover:scale-110"
                >
                  {t("nav.menu")}
                </a>
                <a
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    window.location.href = "#book";
                  }}
                  className="block text-lg text-gray-800 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-400 transition-transform transform hover:scale-110"
                >
                  {t("nav.book")}
                </a>
                <a
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    window.location.href = "#about";
                  }}
                  className="block text-lg text-gray-800 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-400 transition-transform transform hover:scale-110"
                >
                  {t("nav.about")}
                </a>
                <a
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    window.location.href = "#contact";
                  }}
                  className="block text-lg text-gray-800 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-400 transition-transform transform hover:scale-110"
                >
                  {t("nav.contact")}
                </a>
              </div>
              <div className="mt-4 flex items-center space-x-4">
                <ThemeToggle />
                <button
                  onClick={() => setLanguage(language === "en" ? "fi" : "en")}
                  className="bg-amber-600 text-white px-4 py-2 rounded-md hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600 transition-colors"
                >
                  {language === "en" ? "FI" : "EN"}
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}
